/**
 * Persist and control expand/collapse for inbox reply composers.
 * Auto-expands when forceExpandKey changes (new draft / reply / edit).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_PREFIX = "sellyx.inbox.composerExpanded.";

function readStored(storageKey, fallback) {
  if (!storageKey || typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (raw === "0") return false;
    if (raw === "1") return true;
  } catch {
    // ignore storage failures
  }
  return fallback;
}

function writeStored(storageKey, expanded) {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, expanded ? "1" : "0");
  } catch {
    // ignore storage failures
  }
}

/**
 * @param {{
 *   storageKey?: string,
 *   forceExpandKey?: string | number | null,
 *   defaultExpanded?: boolean,
 *   expandOnMount?: boolean,
 * }} options
 */
export function useCollapsibleComposer({
  storageKey = "",
  forceExpandKey = "",
  defaultExpanded = true,
  expandOnMount = false,
} = {}) {
  const [expanded, setExpanded] = useState(() => {
    if (expandOnMount) return true;
    return readStored(storageKey, defaultExpanded);
  });
  const lastForceKey = useRef(null);

  useEffect(() => {
    if (expandOnMount) {
      setExpanded(true);
      writeStored(storageKey, true);
      return;
    }
    setExpanded(readStored(storageKey, defaultExpanded));
  }, [storageKey, defaultExpanded, expandOnMount]);

  useEffect(() => {
    if (forceExpandKey == null || forceExpandKey === "") return;
    if (lastForceKey.current === forceExpandKey) return;
    const hadPrevious = lastForceKey.current != null;
    lastForceKey.current = forceExpandKey;
    if (!hadPrevious) return;
    setExpanded(true);
    writeStored(storageKey, true);
  }, [forceExpandKey, storageKey]);

  const setExpandedPersisted = useCallback((next) => {
    setExpanded(next);
    writeStored(storageKey, next);
  }, [storageKey]);

  const toggle = useCallback(() => {
    setExpandedPersisted(!expanded);
  }, [expanded, setExpandedPersisted]);

  const expand = useCallback(() => {
    setExpandedPersisted(true);
  }, [setExpandedPersisted]);

  const collapse = useCallback(() => {
    setExpandedPersisted(false);
  }, [setExpandedPersisted]);

  return { expanded, setExpanded: setExpandedPersisted, toggle, expand, collapse };
}
